import type { EsFilterDefaultFragment, EsFilterRangeFragment, EsFilterValue } from '#graphql-operations'

type FilterOption = NonNullable<NonNullable<EsFilterDefaultFragment['options']>[number]>
type Filter = (EsFilterDefaultFragment | EsFilterRangeFragment) & {
  activeOptionsCount?: number
  minToBeSelected?: number
  maxToBeSelected?: number
}
type ActiveFilter = Partial<Filter> & {
  key: string
  type?: string
}

interface MetaFilter {
  key: string
  values?: string[]
  __typename?: string
}

interface CatalogMeta {
  filters?: MetaFilter[]
  [key: string]: unknown
}

export function useCatalogFilters(meta: Ref<CatalogMeta>) {
  const { public: config } = useRuntimeConfig()
  const ignoreParamsPattern = new RegExp(config.ignoreParamsPattern)

  const applyOptionsDirectly = useState('applyOptionsDirectly', () => false)
  const rawFilters = useState<Filter[]>('rawFilters', () => [])
  const filters = useState<Filter[]>('filters', () => shallowRef([]))
  const activeFilters = useState<ActiveFilter[]>('activeFilters', () => [])
  const activeFiltersCount = useState<number>('activeFiltersCount', () => 0)

  watch(rawFilters, () => {
    filters.value = [...rawFilters.value]?.filter(filter => !!filter && !filter.isHidden)?.filter(filter => !((filter as EsFilterDefaultFragment).options && !(filter as EsFilterDefaultFragment).options?.length))?.map((filter) => {
      const _filter = {
        ...filter,
      } as Filter
      _filter.activeOptionsCount = 0
      _filter.label = _filter.label.charAt(0).toUpperCase() + _filter.label.slice(1)
      if ('options' in _filter && _filter.options?.length) {
        _filter.options = _filter.options.map((option) => {
          return {
            ...option,
            selected: option.active,
          }
        })?.sort((a, b) => {
          return (a.position ?? 0) - (b.position ?? 0)
        })
        _filter.activeOptionsCount = _filter.options.filter(option => option.active)?.length || 0
      }
      if (_filter.type === 'range') {
        const rangeFilter = _filter as EsFilterRangeFragment & Filter
        rangeFilter.minSelected = rangeFilter.minSelected ?? rangeFilter.min
        rangeFilter.maxSelected = rangeFilter.maxSelected ?? rangeFilter.max
        rangeFilter.minToBeSelected = rangeFilter.minToBeSelected ?? rangeFilter.minSelected
        rangeFilter.maxToBeSelected = rangeFilter.maxToBeSelected ?? rangeFilter.maxSelected
        _filter.activeOptionsCount += Math.floor(rangeFilter.min) !== Math.floor(rangeFilter.minSelected ?? rangeFilter.min) ? 1 : 0
        _filter.activeOptionsCount += Math.ceil(rangeFilter.max) !== Math.ceil(rangeFilter.maxSelected ?? rangeFilter.max) ? 1 : 0
      }
      _filter.isExpanded = _filter.isExpanded || (_filter.activeOptionsCount ?? 0) > 0
      if (_filter.key.includes('brand')) {
        _filter.type = 'default'
      }
      return _filter
    })?.sort((a, b) => {
      return (a.position ?? 0) - (b.position ?? 0)
    }) || []
  }, {
    immediate: true,
  })

  const getDeepestActiveOptions = (options: FilterOption[]): FilterOption[] => {
    const deepest: FilterOption[] = []
    options?.forEach((option) => {
      if (option.active && !option.children?.length) {
        deepest.push(option)
      }
      else if (option.active && option.children) {
        const childDeepest = getDeepestActiveOptions(option.children as FilterOption[])
        if (childDeepest.length) {
          deepest.push(...childDeepest)
        }
        else {
          deepest.push(option)
        }
      }
    })
    return deepest
  }

  watch(filters, () => {
    activeFilters.value = [
      ...filters.value,
    ].filter(filter => (filter.activeOptionsCount ?? 0) > 0).map((f) => {
      const filter = { ...f } as ActiveFilter
      const urlFilter = meta.value?.filters?.find(urlFilter => urlFilter.key === filter.key)

      delete filter.position
      delete filter.hasUrl
      delete filter.isHidden
      delete filter.isExpanded
      delete filter.numberUnfoldedOptions
      delete filter.limitOptionsShowSearchBox
      delete filter.isShowSearchBox

      if (filter.type === 'range') {
        if (filter.min === filter.minSelected) {
          delete filter.minSelected
        }
        delete filter.min
        delete filter.minToBeSelected
        if (filter.max === filter.maxSelected) {
          delete filter.maxSelected
        }
        delete filter.max
        delete filter.maxToBeSelected
      }

      if ('options' in filter && filter.options?.length) {
        filter.options = getDeepestActiveOptions(filter.options as FilterOption[])
      }

      if (urlFilter && ['category', 'cat'].includes(filter.key)) {
        if ('options' in filter && filter.options && filter.options.every(option => urlFilter.values?.includes(option.key))) {
          return null
        }
      }

      return filter
    }).filter((f): f is ActiveFilter => f !== null)
  }, {
    immediate: true,
  })

  watch(activeFilters, () => {
    activeFiltersCount.value = activeFilters.value.length || 0
  }, {
    immediate: true,
  })

  const expectedTotal = useState('expectedTotal', () => 0)
  const route = useRoute()
  const router = useRouter()
  const { search } = useCatalogSearch()
  const { defaultSort, sort, defaultItemsPerPage, itemsPerPage, totalCount } = useCatalogPaginator()
  const { filtersApply: trackFiltersApply } = useDataLayer()
  const { t } = useI18n()

  expectedTotal.value = totalCount.value

  const filtersFromRoute = computed<EsFilterValue[]>(() => {
    const params: EsFilterValue[] = [
      ...(meta.value?.filters?.map((filter) => {
        const { __typename, ...remainingFilterData } = filter
        // Range filter
        if (remainingFilterData?.values?.length === 2 && remainingFilterData?.values?.every(val => !isNaN(+val))) {
          remainingFilterData.min = parseFloat(String(remainingFilterData?.values[0]))
          remainingFilterData.max = parseFloat(String(remainingFilterData?.values[1]))
        }
        return remainingFilterData
      }) || []),
    ]

    Object.entries(route.query).forEach(([filterKey, filterValue]) => {
      if (!filterValue) {
        return
      }
      if (ignoreParamsPattern.test(filterKey)) {
        return
      }

      const filterValues = Array.isArray(filterValue) ? filterValue : String(filterValue).split('~').map((filterValue: string) => decodeURIComponent(filterValue))
      const filter: EsFilterValue = {
        key: filterKey,
        values: filterValues,
      }

      if (filterValues.length === 2 && filterValues.every(val => !isNaN(+val))) {
        filter.min = parseFloat(String(filterValues[0]))
        filter.max = parseFloat(String(filterValues[1]))
      }
      params.push(filter)
    })

    return Object.values(params.reduce<Record<string, EsFilterValue>>((acc, item) => {
      acc[item.key] = acc[item.key] === undefined
        ? item
        : {
            ...acc[item.key],
            ...item,
            values: [
              ...(acc[item.key].values ?? []),
              ...(item.values ?? []),
            ],
          }
      return acc
    }, {}))
  })

  function generateUrlFromSelectedFilters() {
    const basePathPieces = route?.path?.replace(/^\/|\/$/g, '')?.split('/') || []
    const lastBasePathPiece = basePathPieces[basePathPieces.length - 1]
    const params = new URLSearchParams()
    let setFilter = false
    params.set('filter', '1')

    if (search.value) {
      params.set('q', String(search.value))
    }

    filters.value.forEach((filter) => {
      let selectedOptions: FilterOption[] = []
      const urlFilter = meta.value?.filters?.find(urlFilter => urlFilter.key === filter.key)

      switch (filter.type) {
        case 'range':
          {
            const rangeFilter = filter as EsFilterRangeFragment & Filter
            if ((rangeFilter.minSelected && rangeFilter.minSelected !== rangeFilter.min) || (rangeFilter.maxSelected && rangeFilter.maxSelected !== rangeFilter.max)) {
              params.set(filter.key, [rangeFilter.minSelected, rangeFilter.maxSelected].join('~'))
              setFilter = true
            }
          }
          break
        default: {
          let deepestActiveOptions: FilterOption[] = []
          if ('options' in filter && filter.options?.length) {
            deepestActiveOptions = getDeepestActiveOptions(filter.options as FilterOption[])
          }
          selectedOptions = deepestActiveOptions
          if (selectedOptions?.length && urlFilter) {
            selectedOptions = selectedOptions.filter(option => !urlFilter.values?.includes(option.key))
          }
          if (selectedOptions?.length) {
            params.set(filter.key, selectedOptions.map(option => encodeURIComponent(option.key)).join('~'))
            setFilter = true
          }
          if (!selectedOptions?.length && filter.key !== 'category') {
            // Only drop the base path segment (e.g. the theme page slug) when the option
            // that drives it is no longer active. If it's still active but was filtered out
            // above (because it's already represented by the urlFilter/path), keep the path intact.
            const pathOptionStillActive = deepestActiveOptions.some(option => option.urlPath === lastBasePathPiece)
            if ('options' in filter && urlFilter && !pathOptionStillActive && filter.options?.find(option => option.urlPath === lastBasePathPiece)) {
              basePathPieces.pop()
            }
          }
          break
        }
      }
    })

    if (!setFilter || search.value) {
      params.delete('filter')
    }
    if (sort.value !== defaultSort) {
      params.set('sort', sort.value)
    }
    if (itemsPerPage.value !== defaultItemsPerPage) {
      params.set('limit', String(itemsPerPage.value))
    }

    const basePath = '/' + basePathPieces.join('/') + '/'
    const url = basePath + (params.toString().length > 0 ? '?' + params.toString() : '')
    return url
  }

  function rangeUpdate(filterKey: string, min: number, max: number) {
    const filter = filters.value.find(filter => filter.key === filterKey)

    if (filter) {
      filter.minToBeSelected = min
      filter.maxToBeSelected = max
    }

    if (applyOptionsDirectly.value) {
      filterApply(filterKey)
    }
  }

  function optionToggle(filterKey: string, optionKey: string) {
    const filter = filters.value.find(filter => filter.key === filterKey)
    const option = 'options' in filter && filter.options?.find(option => option.key === optionKey)
    if (option) {
      option.selected = !option.selected
    }
    if (applyOptionsDirectly.value) {
      filterApply(filterKey, optionKey)
    }
  }

  // function filterRangeResetMin(filterKey: string) {
  //   const filter = filters.value.find(filter => filter.key === filterKey)
  //   if (filter && 'min' in filter) {
  //     filter.minSelected = filter.min
  //     filter.minToBeSelected = filter.min
  //   }
  // }

  // function filterRangeResetMax(filterKey: string) {
  //   const filter = filters.value.find(filter => filter.key === filterKey)
  //   if (filter && 'max' in filter) {
  //     filter.maxSelected = filter.max
  //     filter.maxToBeSelected = filter.max
  //   }
  // }

  function filterRangeReset(filterKey: string) {
    const filter = filters.value.find(filter => filter.key === filterKey)
    if (filter && 'min' in filter) {
      filter.minSelected = filter.min
      filter.minToBeSelected = filter.min
    }
    if (filter && 'max' in filter) {
      filter.maxSelected = filter.max
      filter.maxToBeSelected = filter.max
    }
  }

  function filterApply(filterKey: string, optionKey?: string) {
    const filter = filters.value.find(filter => filter.key === filterKey) as Filter | undefined

    if (!filter) return

    if (filter.type === 'range') {
      const rangeFilter = filter as EsFilterRangeFragment & Filter
      rangeFilter.minSelected = rangeFilter.minToBeSelected
      rangeFilter.maxSelected = rangeFilter.maxToBeSelected

      trackFiltersApply(`${t(filter.label)}:${rangeFilter.minSelected}-${rangeFilter.maxSelected}`)
    }
    else if ('options' in filter && filter.options?.length) {
      filter.options.forEach((option) => {
        option.active = option.selected
      })

      if (optionKey) {
        const option = filter.options.find(option => option.key === optionKey)
        if (option) {
          trackFiltersApply(`${t(filter.label)}:${option.label}`)
        }
      }
    }

    const url = generateUrlFromSelectedFilters()
    router.push(url)
  }

  function filterReset(filterKey: string) {
    const filter = filters.value.find(filter => filter.key === filterKey)
    if (filter && filter.type === 'range') {
      filter.minToBeSelected = filter.minSelected || filter.min
      filter.maxToBeSelected = filter.maxSelected || filter.max
    }
    else if (filter && 'options' in filter && filter.options?.length) {
      filter.options.forEach((option) => {
        option.selected = option.active
      })
    }
  }

  function filterRemove(filterKey: string) {
    const filter = filters.value.find(filter => filter.key === filterKey)
    if (filter && filter.type === 'range') {
      filter.minToBeSelected = filter.min
      filter.maxToBeSelected = filter.max
    }
    else if (filter && 'options' in filter && filter.options?.length) {
      filter.options.forEach((option) => {
        option.selected = false
      })
    }
  }

  function filtersResetAll() {
    filters.value.forEach((filter) => {
      if (filter.type === 'range') {
        filter.minToBeSelected = filter.minSelected || filter.min
        filter.maxToBeSelected = filter.maxSelected || filter.max
      }
      else if ('options' in filter && filter.options?.length) {
        filter.options.forEach((option) => {
          option.selected = option.active
        })
      }
    })
  }

  function filtersRemoveAll() {
    filters.value.forEach((filter) => {
      if (filter.type === 'range') {
        filter.minToBeSelected = filter.min
        filter.maxToBeSelected = filter.max
      }
      else if ('options' in filter && filter.options?.length) {
        filter.options.forEach((option) => {
          option.selected = false
        })
      }
    })
  }

  function filtersApplyAll() {
    filters.value.forEach((filter) => {
      if (filter.type === 'range') {
        const rangeFilter = filter as EsFilterRangeFragment & Filter
        rangeFilter.minSelected = rangeFilter.minToBeSelected
        rangeFilter.maxSelected = rangeFilter.maxToBeSelected

        if (rangeFilter.minSelected !== rangeFilter.min || rangeFilter.maxSelected !== rangeFilter.max) {
          trackFiltersApply(`${t(filter.label)}:${rangeFilter.minSelected}-${rangeFilter.maxSelected}`)
        }
      }
      else if ('options' in filter && filter.options?.length) {
        const newlySelected = filter.options.filter(opt => opt.selected && !opt.active)

        filter.options.forEach((option) => {
          option.active = option.selected
        })

        newlySelected.forEach((option) => {
          if (filter.key !== 'cat') {
            trackFiltersApply(`${t(filter.label)}:${option.label}`)
          }
        })
      }
    })

    const url = generateUrlFromSelectedFilters()
    router.push(url)
  }

  async function getExpectedTotal() {
    const lclFilters = [
      ...(meta.value?.filters || []),
    ]

    filters.value.filter(filter => !['category'].includes(filter.key)).forEach((filter) => {
      switch (filter.type) {
        case 'range':
          if ((filter.minToBeSelected && filter.minToBeSelected !== filter.min) || (filter.maxToBeSelected && filter.maxToBeSelected !== filter.max)) {
            lclFilters.push({
              key: filter.key,
              min: parseFloat(filter.minToBeSelected),
              max: parseFloat(filter.maxToBeSelected),
            })
          }
          break
        default:
          if ('options' in filter && filter.options?.length) {
            const selectedOptions = filter.options.filter(option => option.selected).map(option => option.key)
            if (selectedOptions.length) {
              lclFilters.push({
                key: filter.key,
                values: selectedOptions,
              })
            }
          }
          break
      }
    })

    const variables = {
      filters: Object.values(Object.assign({}, ...lclFilters.map((f) => {
        const filter = { ...f }
        if (filter.__typename) {
          delete filter.__typename
        }
        return { [filter.key]: filter }
      }))),
      search: search.value,
    }

    const { data: response } = await useAsyncGraphqlQuery('esProductListCount', variables, useGraphqlDefaultClient())
    expectedTotal.value = response?.value?.data?.esProductList?.totalCount || 0
    return response?.value?.data?.esProductList?.totalCount
  }

  const urlFilterKeys = computed(() => meta.value.filters?.map((item: MetaFilter) => item.key) || [])

  const filtersOnQuery = computed(() => {
    return filters.value.filter((item: any) => !urlFilterKeys.value.includes(item.key || 'unknown'))
  })

  const activeFiltersOnQuery = computed(() => {
    return activeFilters.value.filter((item: any) => !urlFilterKeys.value.includes(item.key || 'unknown'))
  })

  const activeFiltersCountOnQuery = computed(() => {
    return activeFiltersOnQuery.value.length
  })

  return {
    generateUrlFromSelectedFilters,
    applyOptionsDirectly,
    optionToggle,
    rangeUpdate,
    filterRangeReset,
    // filterRangeResetMin,
    // filterRangeResetMax,
    filterApply,
    filterReset,
    filterRemove,
    rawFilters,
    filters,
    filtersApplyAll,
    filtersResetAll,
    filtersRemoveAll,
    filtersFromRoute,
    activeFilters,
    activeFiltersCount,
    expectedTotal,
    getExpectedTotal,
    filtersOnQuery,
    activeFiltersOnQuery,
    activeFiltersCountOnQuery,
    urlFilterKeys,
  }
}
